import gql from 'graphql-tag';

import { type CustomerPersona, mapPersonaForBackendInput } from 'src/data/persona';
import { executeGqlMutation } from 'src/graphql/client';
import { stringifyIfPresent } from 'src/lib/common';
import { getSingleProp } from 'src/lib/graphql';
import type { BackendJob, BackendMutationGenerateMultiPromptAssetsArgs } from 'src/types/backend';

type Variables = Omit<BackendMutationGenerateMultiPromptAssetsArgs, 'customerPersona'> & {
  customerPersona?: CustomerPersona;
};

type FeedJob = Pick<BackendJob, 'id' | 'status'>;

interface Options {
  abortSignal?: AbortSignal;
}

const MUTATION = gql`
  mutation GenerateMultiPromptAssets(
    $projectId: ID!
    $sectionId: ID!
    $generationTool: GenerationToolEnum!
    $customMultiPromptId: ID
    $promptFields: JSON
    $promptFieldImages: JSON
    $promptFieldDocuments: JSON
    $scoringMethod: ScoringMethodInput
    $suggestionMode: String
    $language: String
    $numberOfVariations: Int
    $customerPersona: CustomerPersonaInput
    $annotations: [SuggestionAnnotationInput!]
    $structureInstructionsData: JSON
    $blocksSettings: JSON
    $urlExtractions: [UrlExtractionInput!]
    $csvId: ID
    $languageTargetFormality: LanguageTargetFormalityEnum
    $associatedResources: [AssociatedResourceInput!]
  ) {
    generateMultiPromptAssets(
      projectId: $projectId
      sectionId: $sectionId
      generationTool: $generationTool
      customMultiPromptId: $customMultiPromptId
      promptFields: $promptFields
      promptFieldImages: $promptFieldImages
      promptFieldDocuments: $promptFieldDocuments
      scoringMethod: $scoringMethod
      suggestionMode: $suggestionMode
      language: $language
      numberOfVariations: $numberOfVariations
      customerPersona: $customerPersona
      annotations: $annotations
      structureInstructionsData: $structureInstructionsData
      blocksSettings: $blocksSettings
      urlExtractions: $urlExtractions
      csvId: $csvId
      languageTargetFormality: $languageTargetFormality
      associatedResources: $associatedResources
    ) {
      id
      status
    }
  }
`;

/**
 * Builds the chain, pre-creates the group the run lands in, and enqueues it — the
 * `setupMultiPromptRunner` / `runMultiPromptRunnerAsync` / `getMultiPromptRunner`
 * trio in one call, with a `sectionId` for where the card belongs.
 *
 * `blocksSettings` is keyed by the step's index rather than by block id: the chain is built by this
 * same call, so the ids do not exist yet. A step left out of a non-empty payload is archived.
 *
 * `urlExtractions` carries every scraped page the run may read: the backend resolves a URL
 * placeholder in a prompt field and in a step's own prompt from this one list.
 *
 * `csvId` names the uploaded sheet a CSV template runs over, and is sent only for one.
 *
 * `associatedResources` are attached to the run rather than to a field, and the backend adds them
 * to the context of every step.
 */
export const generateMultiPromptAssets = async (
  { customerPersona, structureInstructionsData, blocksSettings, ...rest }: Variables,
  options: Options | null = null
): Promise<FeedJob | null> => {
  const response = await executeGqlMutation({
    mutation: MUTATION,
    variables: {
      ...rest,
      customerPersona: customerPersona ? mapPersonaForBackendInput(customerPersona) : undefined,
      structureInstructionsData: stringifyIfPresent(structureInstructionsData),
      blocksSettings: stringifyIfPresent(blocksSettings),
    },
    options: {
      abortSignal: options?.abortSignal,
      traceAcRequests: true,
    },
  });

  return getSingleProp<FeedJob>(response.data) ?? null;
};
